import { FC, useMemo } from "react";
import { Form, Field } from "react-final-form";
import { _ } from "i18n";
import { AdminSelectAdapter } from "components/select";
import type {
	TeamGroupClubResponse,
	TeamGroupRowResponse,
	TeamGroupTeamResponse,
	TeamGroupUpsertRequest,
} from "libs/apiClient";
import {
	formatTeamGroupClubOptionLabel,
	formatTeamGroupTeamOptionLabel,
} from "./teamGroupOptionLabels";

interface SelectOption {
	label: string;
	value: string;
}

type TeamGroupBooleanOptionField = "scoutVisibility" | "sharedAnalysis" | "visibleTeamsheets";

interface TeamGroupFormValues {
	name: string;
	notes: string;
	scoutVisibility: boolean | undefined;
	sharedAnalysis: boolean | undefined;
	visibleTeamsheets: boolean | undefined;
	teamIds: SelectOption[];
	clubIds: SelectOption[];
}

interface TeamGroupFormErrors {
	name?: string;
	scoutVisibility?: string;
	sharedAnalysis?: string;
	visibleTeamsheets?: string;
	clubIds?: string;
	teamIds?: string;
}

interface TeamGroupYesNoFieldProps {
	name: TeamGroupBooleanOptionField;
	label: string;
	showErrors: boolean;
}

function TeamGroupYesNoField({ name, label, showErrors }: TeamGroupYesNoFieldProps) {
	return (
		<Field name={name}>
			{({ input, meta }) => (
				<div className="subscriptionEditForm-yesNoField">
					<div className="cellTitle cellTitle--required">{label}</div>
					<div className="subscriptionEditForm-discoverCountriesRadios">
						<label className="subscriptionEditForm-discoverCountriesRadio">
							<input
								type="radio"
								name={input.name}
								checked={input.value === true}
								onChange={() => input.onChange(true)}
							/>
							<span>Yes</span>
						</label>
						<label className="subscriptionEditForm-discoverCountriesRadio">
							<input
								type="radio"
								name={input.name}
								checked={input.value === false}
								onChange={() => input.onChange(false)}
							/>
							<span>No</span>
						</label>
					</div>
					{meta.error && showErrors ? <span className="form-error">{meta.error}</span> : null}
				</div>
			)}
		</Field>
	);
}

function TeamGroupRequiredMultiSelectField({
	name,
	label,
	options,
	showErrors,
}: {
	name: "clubIds" | "teamIds";
	label: string;
	options: SelectOption[];
	showErrors: boolean;
}) {
	return (
		<div className="subscriptionEditForm-field subscriptionEditForm-field--full">
			<div className="cellTitle cellTitle--required">{label}</div>
			<div className="subscriptionEditForm-input">
				<Field name={name}>
					{({ input, meta }) => (
						<>
							<AdminSelectAdapter input={input} isMulti options={options} />
							{meta.error && showErrors ? <span className="form-error">{meta.error}</span> : null}
						</>
					)}
				</Field>
			</div>
		</div>
	);
}

function validateTeamGroupForm(values: TeamGroupFormValues): TeamGroupFormErrors {
	const errors: TeamGroupFormErrors = {};
	const requiredSelectionMessage = "Please select Yes or No";
	const clubsOrTeamsMessage = "Select at least one club or team";
	const trimmedName = (values.name ?? "").trim();

	if (trimmedName === "") {
		errors.name = "Team Group Name is required";
	} else if (trimmedName.length > 180) {
		errors.name = "Team Group Name must be at most 180 characters";
	}

	if (values.scoutVisibility === undefined) {
		errors.scoutVisibility = requiredSelectionMessage;
	}

	if (values.sharedAnalysis === undefined) {
		errors.sharedAnalysis = requiredSelectionMessage;
	}

	if (values.visibleTeamsheets === undefined) {
		errors.visibleTeamsheets = requiredSelectionMessage;
	}

	const clubIds = values.clubIds ?? [];
	const teamIds = values.teamIds ?? [];

	if (clubIds.length === 0 && teamIds.length === 0) {
		errors.clubIds = clubsOrTeamsMessage;
		errors.teamIds = clubsOrTeamsMessage;
	}

	return errors;
}

interface TeamGroupFormProps {
	row?: TeamGroupRowResponse;
	discoverTeamOptions: SelectOption[];
	discoverClubOptions: SelectOption[];
	submitError?: string | null;
	isSubmitting?: boolean;
	onSubmit: (payload: TeamGroupUpsertRequest) => Promise<void>;
}

function mergeTeamOptions(
	discoverTeamOptions: SelectOption[],
	existingTeams: TeamGroupTeamResponse[],
): SelectOption[] {
	const optionByValue = new Map<string, SelectOption>();

	for (const option of discoverTeamOptions) {
		optionByValue.set(option.value, option);
	}

	for (const team of existingTeams) {
		const value = String(team.id);
		if (optionByValue.has(value) === false) {
			optionByValue.set(value, {
				label: formatTeamGroupTeamOptionLabel(team.name ?? "", {
					country: team.country,
				}),
				value,
			});
		}
	}

	return [...optionByValue.values()];
}

function mergeClubOptions(
	discoverClubOptions: SelectOption[],
	existingClubs: TeamGroupClubResponse[],
): SelectOption[] {
	const optionByValue = new Map<string, SelectOption>();

	for (const option of discoverClubOptions) {
		optionByValue.set(option.value, option);
	}

	for (const club of existingClubs) {
		const value = String(club.id);
		if (optionByValue.has(value) === false) {
			optionByValue.set(value, {
				label: formatTeamGroupClubOptionLabel(club.id, club.name ?? "", club.countryCode),
				value,
			});
		}
	}

	return [...optionByValue.values()];
}

function buildInitialValues(row: TeamGroupRowResponse | undefined): TeamGroupFormValues {
	if (row === undefined) {
		return {
			name: "",
			notes: "",
			scoutVisibility: undefined,
			sharedAnalysis: undefined,
			visibleTeamsheets: undefined,
			teamIds: [],
			clubIds: [],
		};
	}

	return {
		name: row.name ?? "",
		notes: row.notes ?? "",
		scoutVisibility: row.scoutVisibility === true,
		sharedAnalysis: row.sharedAnalysis === true,
		visibleTeamsheets: row.visibleTeamsheets === true,
		teamIds: (row.teams ?? []).map((team) => ({
			label: formatTeamGroupTeamOptionLabel(team.name ?? "", {
				country: team.country,
			}),
			value: String(team.id),
		})),
		clubIds: (row.clubs ?? []).map((club) => ({
			label: formatTeamGroupClubOptionLabel(club.id, club.name ?? "", club.countryCode),
			value: String(club.id),
		})),
	};
}

function buildPayload(values: TeamGroupFormValues): TeamGroupUpsertRequest {
	const trimmedNotes = (values.notes ?? "").trim();

	return {
		name: (values.name ?? "").trim(),
		notes: trimmedNotes === "" ? null : trimmedNotes,
		scoutVisibility: values.scoutVisibility === true,
		sharedAnalysis: values.sharedAnalysis === true,
		visibleTeamsheets: values.visibleTeamsheets === true,
		teamIds: (values.teamIds ?? [])
			.map((option) => Number.parseInt(option.value, 10))
			.filter((teamId) => Number.isFinite(teamId)),
		clubIds: (values.clubIds ?? [])
			.map((option) => Number.parseInt(option.value, 10))
			.filter((clubId) => Number.isFinite(clubId)),
	};
}

export const TeamGroupForm: FC<TeamGroupFormProps> = ({
	row,
	discoverTeamOptions,
	discoverClubOptions,
	submitError = null,
	isSubmitting = false,
	onSubmit,
}) => {
	const teamOptions = useMemo(
		() => mergeTeamOptions(discoverTeamOptions, row?.teams ?? []),
		[discoverTeamOptions, row?.teams],
	);
	const clubOptions = useMemo(
		() => mergeClubOptions(discoverClubOptions, row?.clubs ?? []),
		[discoverClubOptions, row?.clubs],
	);
	const initialValues = useMemo(() => buildInitialValues(row), [row]);

	return (
		<Form
			initialValues={initialValues}
			validate={validateTeamGroupForm}
			onSubmit={async (values: TeamGroupFormValues) => {
				await onSubmit(buildPayload(values));
			}}
			render={({ handleSubmit, submitting, submitFailed }) => {
				const showValidationErrors = submitFailed;

				return (
				<form className="subscriptionEditForm" onSubmit={handleSubmit}>
					{submitError && <div className="form-error-banner">{submitError}</div>}

					<section className="subscriptionEditForm-section">
						<div className="subscriptionEditForm-grid">
							<div className="subscriptionEditForm-field">
								<div className="cellTitle cellTitle--required">Team Group Name</div>
								<div className="subscriptionEditForm-input">
									<Field name="name">
										{({ input, meta }) => (
											<>
												<input {...input} className="form-control" maxLength={180} />
												{meta.error && (meta.touched || showValidationErrors) ? (
													<span className="form-error">{meta.error}</span>
												) : null}
											</>
										)}
									</Field>
								</div>
							</div>

							<div className="subscriptionEditForm-field subscriptionEditForm-field--full">
								<div className="cellTitle">Notes</div>
								<div className="subscriptionEditForm-input">
									<Field name="notes" component="textarea" className="form-control" rows={3} />
								</div>
							</div>

							<div className="subscriptionEditForm-field subscriptionEditForm-field--full">
								<div className="subscriptionEditForm-input subscriptionEditForm-yesNoFields">
									<TeamGroupYesNoField
										name="scoutVisibility"
										label="Scout visibility - data is visible to scouts with a Discover subscription"
										showErrors={showValidationErrors}
									/>
									<TeamGroupYesNoField
										name="sharedAnalysis"
										label="Shared analysis"
										showErrors={showValidationErrors}
									/>
									<TeamGroupYesNoField
										name="visibleTeamsheets"
										label="Visible teamsheets"
										showErrors={showValidationErrors}
									/>
								</div>
							</div>

							<TeamGroupRequiredMultiSelectField
								name="clubIds"
								label="Clubs"
								options={clubOptions}
								showErrors={showValidationErrors}
							/>

							<TeamGroupRequiredMultiSelectField
								name="teamIds"
								label="Teams"
								options={teamOptions}
								showErrors={showValidationErrors}
							/>
						</div>
					</section>

					<div className="subscriptionEditForm-actions">
						<button type="submit" className="btn btn-light-blue" disabled={submitting || isSubmitting}>
							{_("btn.save")}
						</button>
					</div>
				</form>
				);
			}}
		/>
	);
};

export default TeamGroupForm;
